import { type Point2, isSamePoint } from './point';

export type NodeIndex = {
  positions: Point2[];
  nodeFor(point: Point2): number;
};

/**
 * Welds coincident points into shared nodes.
 *
 * A hash grid at the tolerance scale, probing the 3×3 neighbourhood on lookup.
 * Probing the neighbours is the point: bucketing alone would fail to weld two
 * points a nanometre apart that happen to straddle a cell boundary, which is
 * exactly the case this exists to handle.
 */
export const createNodeIndex = (tolerance: number): NodeIndex => {
  const cell = Math.max(tolerance, Number.MIN_VALUE);
  const buckets = new Map<string, number[]>();
  const positions: Point2[] = [];
  const keyOf = (cx: number, cy: number): string => `${cx},${cy}`;

  return {
    positions,
    nodeFor(point: Point2): number {
      const cx = Math.floor(point[0] / cell);
      const cy = Math.floor(point[1] / cell);
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          const bucket = buckets.get(keyOf(cx + dx, cy + dy));
          if (!bucket) {
            continue;
          }
          for (let index = 0; index < bucket.length; index += 1) {
            const candidate = bucket[index];
            if (candidate === undefined) {
              continue;
            }
            const position = positions[candidate];
            if (position && isSamePoint(position, point, tolerance)) {
              return candidate;
            }
          }
        }
      }
      const node = positions.length;
      positions.push(point);
      const key = keyOf(cx, cy);
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push(node);
      } else {
        buckets.set(key, [node]);
      }
      return node;
    },
  };
};
